
import React, { useEffect, useMemo } from 'react';
import { Check, ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query'; // Integrated TanStack
import '../Style/ECUFotaDeploymentStatus.css';

const API_BASE_URL = "https://api.yellowmatrix.co.in"; 

const stepColors = [
  "#64748B", "#9A3412", "#38BDF8", "#7C3AED", "#312E81", "#3B82F6", "#41ca76"
];

// --- Sub-component: Individual Progress Step ---
const Step = ({ step }) => {
  const { label, status, progress = 30, color } = step;

  const renderIndicator = () => {
    if (status === "completed") {
      return (
        <div className="progress-step-indicator-ECUStatus completed-ECUStatus" style={{ backgroundColor: color, border: `2px solid ${color}` }}>
          <Check className="icon-size-default-ECUStatus" color="#ffffff" />
        </div>
      );
    } else if (status === "inProgress") {
      const circumference = 2 * Math.PI * 11;
      const offset = circumference - (progress / 100) * circumference;
      return (
        <div className="progress-step-circle-wrapper-ECUStatus">
          <svg className="progress-circle-svg-ECUStatus" viewBox="0 0 24 24">
            <circle className="progress-circle-base-path-ECUStatus" strokeWidth="2" stroke="#E5E7EB" fill="transparent" r="11" cx="12" cy="12" />
            <circle strokeWidth="2" strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" fill="transparent" r="11" cx="12" cy="12" transform="rotate(-90 12 12)" className="progress-circle-path-ECUStatus" style={{ stroke: color }} />
          </svg>
          <span className="progress-circle-text-ECUStatus" style={{ color: color, position: 'absolute', fontSize: '10px', fontWeight: 'bold' }}>{progress}%</span>
        </div>
      );
    } else {
      return (
        <div className="progress-step-indicator-ECUStatus pending-ECUStatus" style={{ borderColor: '#D1D5DB', backgroundColor: '#FFFFFF', border: '2px solid' }}></div>
      );
    }
  };

  return (
    <div className="progress-step-item-ECUStatus">
      {renderIndicator()}
      <div className={`progress-step-label-ECUStatus ${status === "inProgress" ? 'current-status-ECUStatus' : ''}`} style={{ color: (status === "completed" || status === "inProgress") ? color : '#6B7280', fontWeight: 'bold', whiteSpace:'nowrap' }}>
        {label}
      </div>
    </div>
  );
};

// --- Sub-component: Progress Bar Container ---
const ProgressBar = ({ steps }) => (
  <div className="progress-tracker-container-ECUStatus">
    {steps.map((step, index) => (
      <React.Fragment key={index}>
        <Step step={step} />
        {index < steps.length - 1 && (
          <div className="progress-connecting-line-ECUStatus" style={{ backgroundColor: (step.status === "completed" || step.status === "inProgress") ? step.color : '#D1D5DB', height: '2px', flex: 1, margin: '0 10px' }}></div>
        )}
      </React.Fragment>
    ))}
  </div>
);

// --- Sub-component: Phases Completed Container ---
const PhasesCompleted = ({ phasesData, totalDevices }) => {
  const chartData = useMemo(() => {
    if (!phasesData) return [];
    return [
      { name: "Pending", value: phasesData.pie["Pending"] || 0, color: "#A0AEC0" },
      { name: "In Progress", value: phasesData.pie["In Progress"] || 0, color: "#4299E1" },
      { name: "Aborted", value: phasesData.pie["Aborted"] || 0, color: "#FC8181" },
      { name: "Failed", value: phasesData.pie["Failed"] || 0, color: "#F56565" },
      { name: "Complete", value: phasesData.pie["Complete"] || 0, color: "#48BB78" },
    ];
  }, [phasesData]);

  const progressBars = useMemo(() => {
    if (!phasesData) return [];
    const maxDev = totalDevices || 1;
    return [
      { label: "Request Received", current: phasesData.bars["Request Received"] || 0, max: maxDev, color: "#8B5F29" },
      { label: "Package Downloading", current: phasesData.bars["Package Downloading"] || 0, max: maxDev, color: "#4299E1" },
      { label: "Update Staging", current: phasesData.bars["Update Staging"] || 0, max: maxDev, color: "#A0AEC0" },
      { label: "Waiting For Preconditions", current: phasesData.bars["Waiting For Preconditions"] || 0, max: maxDev, color: "#A0AEC0" },
      { label: "Update In Progress", current: phasesData.bars["Update In Progress"] || 0, max: maxDev, color: "#A0AEC0" },
    ];
  }, [phasesData, totalDevices]);

  useEffect(() => {
    const canvas = document.getElementById("phasesPieChart");
    if (!canvas || chartData.length === 0) return;
    const ctx = canvas.getContext("2d");
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) * 0.9;
    let total = chartData.reduce((sum, item) => sum + item.value, 0);
    let startAngle = -Math.PI / 2;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (total === 0) { 
      ctx.beginPath(); ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI); ctx.fillStyle = "#E2E8F0"; ctx.fill(); return;
    }
    chartData.forEach(slice => {
      if (slice.value === 0) return;
      const sliceAngle = (slice.value / total) * 2 * Math.PI;
      ctx.beginPath(); ctx.moveTo(centerX, centerY); ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle); ctx.closePath(); ctx.fillStyle = slice.color; ctx.fill();
      startAngle += sliceAngle;
    });
  }, [chartData]);

  if (!phasesData) return null;

  return (
    <div className="phases-card-layout-ECUStatus">
      <div className="phases-chart-column-ECUStatus">
        <canvas id="phasesPieChart" width="180" height="180"></canvas>
        <div className="phases-legend-ECUStatus">
          {chartData.map((item, idx) => (
            <div key={idx} className="legend-row-ECUStatus">
              <div className="legend-label-group-ECUStatus">
                <span className="legend-dot-ECUStatus" style={{ backgroundColor: item.color }}></span>
                <span className="legend-name-ECUStatus">{item.name}</span>
              </div>
              <span className="legend-val-ECUStatus">{item.value}</span>
            </div>
          ))}
          <div className="targeted-total-ECUStatus"><span>Total Vehicles Targeted</span><strong>{totalDevices}</strong></div>
        </div>
      </div>
      <div className="phases-bars-column-ECUStatus">
        {progressBars.map((bar, idx) => (
          <div key={idx} className="bar-row-ECUStatus">
            <span className="bar-label-ECUStatus">{bar.label}</span>
            <div className="bar-track-ECUStatus"><div className="bar-fill-ECUStatus" style={{ width: `${(bar.current / bar.max) * 100}%`, backgroundColor: bar.color }}></div></div>
            <span className="bar-stats-ECUStatus">{bar.current}/{bar.max}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- Main Component ---
const ECUFotaDeploymentStatus = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Use TanStack Query for data fetching and background refreshing
  const { data: deploymentData, isLoading } = useQuery({
    queryKey: ['ecuDeploymentStatus', id],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/ecu-fota/${id}/deployment-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to load details");
      return res.json();
    },
    refetchInterval: 5000, // Sync every 5 seconds
    enabled: !!id
  });

  if (isLoading && !deploymentData) return <div style={{padding: '40px', textAlign: 'center'}}>Loading deployment details...</div>;
  if (!deploymentData) return <div style={{padding: '40px', textAlign: 'center'}}>No data found.</div>;

  const { overview, steps, phases } = deploymentData;

  // --- YOUR ORIGINAL WATERFALL FIX LOGIC ---
  let furthestActiveStep = -1;
  steps.forEach((step, index) => {
    if (step.status === "completed" || step.status === "inProgress") {
      furthestActiveStep = Math.max(furthestActiveStep, index);
    }
  });

  const coloredSteps = steps.map((s, index) => {
    let enforcedStatus = s.status;
    if (index < furthestActiveStep) enforcedStatus = "completed";
    if (index === furthestActiveStep && index !== steps.length - 1 && overview.success_device_count < overview.device_count) {
       enforcedStatus = "inProgress";
    }
    return {
      ...s,
      status: enforcedStatus,
      color: stepColors[index] || "#3B82F6",
      progress: enforcedStatus === "inProgress" ? 30 : null
    };
  });

  return (
    <div className="deployment-status-app-container-ECUStatus">
      <header className="deployment-status-header-ECUStatus">
        <h1 className="deployment-status-header-title-ECUStatus">Deployment Status</h1>
        <div className="deployment-status-nav-container-ECUStatus">
          <nav className="deployment-status-nav-ECUStatus">
            <button className="deployment-status-nav-link-ECUStatus gray-text-ECUStatus" onClick={() => navigate("/ECUDeploymentStatus")}> 
              <ArrowLeft size={16} style={{marginRight: '5px'}}/>Back
            </button>
          </nav>
        </div>
      </header>

      <section className="deployment-status-section-ECUStatus">
        <h2 className="deployment-status-section-title-ECUStatus">Overview</h2>
        <ProgressBar steps={coloredSteps} />
        <div className="overview-details-grid-ECUStatus">
          <div><p className="overview-detail-label-ECUStatus">Firmware Name</p><p>{overview.firmware_name}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Device Group</p><p>{overview.device_group}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Version</p><p>{overview.version}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Created By</p><p>{overview.created_by}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Start Time</p><p>{new Date(overview.start_time).toLocaleString()}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Device Count</p><p>{overview.device_count}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Success</p><p>{overview.success_device_count}</p></div>
          <div><p className="overview-detail-label-ECUStatus">Failed</p><p>{overview.fail_device_count}</p></div>
        </div>
      </section>

      <section className="deployment-status-section-ECUStatus">
        <h2 className="deployment-status-section-title-ECUStatus">Phases Completed</h2>
        <PhasesCompleted phasesData={phases} totalDevices={overview.device_count} />
      </section>
    </div>
  );
};

export default ECUFotaDeploymentStatus;

